/**
 * CoachPlaybook - Side Panel Component
 * Version: 0.5.145
 * 
 * v0.5.105 Changes:
 * - Added version display at bottom of sidebar for verification
 * 
 * v0.5.77 Changes:
 * - Pending defender pulses with animation when awaiting position
 * - Removed guarding dropdown (auto-assigned by position)
 * - Removed stop following option
 */

import React, { useState, useRef, useEffect } from 'react';

const SidePanel = ({
  positions = [],
  screens = [],
  defenders = [],
  defenderPaths = [],
  paths = [],
  passes = [],
  selectedPosition,
  selectedDefender,
  pendingDefender = null, // v0.5.76: Defender waiting for position (will pulse)
  playerSize = 0.6,
  defenderSize = 0.6,
  showDefenders = true,
  showJerseys = true,
  rosterPlayers = [],
  defenderJerseyColor = '#dc2626',
  opponents = [],
  selectedOpponentId = null,
  currentStep = 0,
  onShowDefendersChange,
  onShowJerseysChange,
  onDefenderJerseyColorChange,
  onSelectedOpponentChange,
  onPositionSelect,
  onPlayerSizeChange,
  onDefenderSizeChange,
  onScreenUpdate,
  onPlayerUpdate,
  onDefenderUpdate,
  onDefenderSelect,
  onDefenderPathStart,
  onAddDefender,
  onRemoveDefender
}) => {
  const [expandedSections, setExpandedSections] = useState({
    players: true,
    screens: false,
    defenders: false,
    actions: false
  });
  
  const scrollContainerRef = useRef(null);
  const placedPositions = positions.filter(p => p.startX !== null && p.startX !== undefined);
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Collect all play actions
  const allActions = [
    ...(paths || []).map(p => ({
      type: p.isScreenMove ? 'screen-move' : p.type || 'movement',
      step: p.stepNumber || 1,
      player: positions.find(pos => pos.id === p.positionId)?.positionLabel || '?',
      color: positions.find(pos => pos.id === p.positionId)?.color || '#666'
    })),
    ...(passes || []).map(p => ({
      type: 'pass',
      step: p.stepNumber || 1,
      player: positions.find(pos => pos.id === p.fromPositionId)?.positionLabel || '?',
      to: positions.find(pos => pos.id === p.toPositionId)?.positionLabel || '?',
      color: positions.find(pos => pos.id === p.fromPositionId)?.color || '#666'
    })),
    ...(screens || []).map(s => ({
      type: 'screen',
      step: s.stepNumber || 1,
      player: positions.find(pos => pos.id === s.setterId)?.positionLabel || '?',
      color: positions.find(pos => pos.id === s.setterId)?.color || '#666',
      name: s.name
    })),
    ...(defenderPaths || []).map(p => ({
      type: 'defender-move',
      step: p.stepNumber || 1,
      defender: `D${(defenders.findIndex(d => d.id === p.defenderId) + 1) || '?'}`,
      color: '#dc2626'
    }))
  ].sort((a, b) => a.step - b.step);

  // Section header component for consistency
  const SectionHeader = ({ id, icon, title, count, color, isExpanded }) => (
    <button
      onClick={() => toggleSection(id)}
      className={`
        w-full flex items-center justify-between px-4 py-3
        font-semibold text-sm transition-all duration-200
        ${isExpanded ? color : 'bg-slate-800 hover:bg-slate-700'}
        sticky top-0 z-10
      `}
      style={{ minHeight: '48px' }}
    >
      <span className="flex items-center gap-3">
        <span className="text-lg">{icon}</span>
        <span className="text-white">{title}</span>
      </span>
      <span className="flex items-center gap-2">
        {count !== undefined && (
          <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs text-white">
            {count}
          </span>
        )}
        <span className={`text-white transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
          ▼
        </span>
      </span>
    </button>
  );

  return (
    <div 
      ref={scrollContainerRef}
      className="flex flex-col bg-slate-900 rounded-xl overflow-hidden"
      style={{ 
        height: '100%',
        maxHeight: 'calc(100vh - 200px)',
        minHeight: '300px'
      }}
    >
      {/* Scrollable content */}
      <div 
        className="flex-1 overflow-y-auto overflow-x-hidden"
        style={{ 
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'thin',
          scrollbarColor: '#475569 #1e293b'
        }}
      >
        {/* ===== PLAYERS SECTION ===== */}
        <div className="border-b border-slate-700">
          <SectionHeader
            id="players"
            icon="👥"
            title="Players"
            count={`${placedPositions.length}/5`}
            color="bg-blue-600"
            isExpanded={expandedSections.players}
          />
          
          {expandedSections.players && (
            <div className="p-3 bg-slate-800/50 space-y-3">
              {/* Size Control */}
              <div className="flex items-center gap-3 p-3 bg-slate-900 rounded-lg">
                <span className="text-xs text-slate-400 w-10">Size</span>
                <input
                  type="range"
                  min="0.5"
                  max="1.2"
                  step="0.1"
                  value={playerSize}
                  onChange={(e) => onPlayerSizeChange?.(parseFloat(e.target.value))}
                  className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                />
                <span className="text-sm text-blue-400 font-bold w-10 text-right">
                  {playerSize.toFixed(1)}
                </span>
              </div>
              
              {/* Player List */}
              {placedPositions.length === 0 ? (
                <div className="text-center py-6 text-slate-500 text-sm">
                  Drag players onto the court
                </div>
              ) : (
                <div className="space-y-2">
                  {placedPositions.map((pos) => {
                    const playerSpeed = pos.speed || 1.0;
                    const speedColor = playerSpeed < 0.8 ? '#ef4444' : playerSpeed > 1.2 ? '#22c55e' : '#f59e0b';
                    const isSelected = selectedPosition === pos.id;
                    
                    return (
                      <div
                        key={pos.id}
                        className={`rounded-lg overflow-hidden transition-all ${
                          isSelected ? 'ring-2 ring-blue-500 bg-blue-900/30' : 'bg-slate-900'
                        }`}
                      >
                        {/* Player Row - Clickable */}
                        <button
                          onClick={() => onPositionSelect?.(pos)}
                          className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-800/50 transition-colors"
                          style={{ minHeight: '56px' }}
                        >
                          {/* Avatar */}
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg flex-shrink-0"
                            style={{ backgroundColor: pos.color }}
                          >
                            {pos.positionLabel}
                          </div>
                          
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="text-white font-medium truncate">
                              {pos.playerName || pos.positionLabel}
                            </div>
                            <div className="text-xs text-slate-400">
                              {pos.jerseyNumber ? `#${pos.jerseyNumber}` : 'Not assigned'}
                            </div>
                          </div>
                          
                          {/* Speed Badge */}
                          {playerSpeed !== 1.0 && (
                            <div 
                              className="px-2 py-1 rounded text-xs font-bold"
                              style={{ backgroundColor: speedColor + '30', color: speedColor }}
                            >
                              {playerSpeed.toFixed(1)}x
                            </div>
                          )}
                        </button>
                        
                        {/* v0.5.71: Roster Player Assignment - shows current player or dropdown when selected */}
                        {rosterPlayers.length > 0 && (
                          <div className="px-3 py-2 bg-slate-950/50 border-t border-slate-800">
                            {isSelected ? (
                              <>
                                <label className="text-xs text-slate-400 block mb-1">Assign Player</label>
                                <select
                                  value={pos.rosterId || ''}
                                  onChange={(e) => {
                                    const rosterId = e.target.value;
                                    const rosterPlayer = rosterPlayers.find(r => r.id === rosterId);
                                    onPlayerUpdate?.(pos.id, {
                                      rosterId: rosterId || null,
                                      playerName: rosterPlayer?.name || null,
                                      jerseyNumber: rosterPlayer?.jerseyNumber || null
                                    });
                                  }}
                                  className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                                >
                                  <option value="">-- No Player --</option>
                                  {rosterPlayers.map(player => (
                                    <option key={player.id} value={player.id}>
                                      #{player.jerseyNumber} {player.name}
                                    </option>
                                  ))}
                                </select>
                              </>
                            ) : pos.rosterId ? (
                              <div className="text-xs text-slate-400">
                                👤 #{pos.jerseyNumber} {pos.playerName}
                              </div>
                            ) : null}
                          </div>
                        )}
                        
                        {/* Speed Slider */}
                        <div className="flex items-center gap-2 px-3 py-2 bg-slate-950/50 border-t border-slate-800">
                          <span className="text-xs text-slate-500 w-8">
                            {playerSpeed < 1 ? '🐢' : playerSpeed > 1 ? '🐇' : '⚡'}
                          </span>
                          <input
                            type="range"
                            min="0.25"
                            max="2.0"
                            step="0.25"
                            value={playerSpeed}
                            onChange={(e) => onPlayerUpdate?.(pos.id, { speed: parseFloat(e.target.value) })}
                            className="flex-1 h-1.5 bg-slate-700 rounded appearance-none cursor-pointer"
                            style={{ accentColor: speedColor }}
                          />
                          <span 
                            className="text-xs font-bold w-12 text-right"
                            style={{ color: speedColor }}
                          >
                            {playerSpeed.toFixed(2)}x
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* v0.5.39: Screens section moved to StepActionsPanel */}

        {/* ===== DEFENDERS SECTION ===== */}
        <div className="border-b border-slate-700">
          <SectionHeader
            id="defenders"
            icon="🔴"
            title="Defenders"
            count={defenders.length}
            color="bg-red-600"
            isExpanded={expandedSections.defenders}
          />
          
          {expandedSections.defenders && (
            <div className="p-3 bg-slate-800/50 space-y-3">
              {/* Show/Hide Toggle */}
              <div className="flex items-center justify-between p-3 bg-slate-900 rounded-lg">
                <span className="text-sm text-white">Show Defenders</span>
                <button
                  onClick={() => onShowDefendersChange?.(!showDefenders)}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    showDefenders ? 'bg-red-500' : 'bg-slate-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    showDefenders ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
              
              {/* v0.5.71: Defender Jersey - Opponent Team Selection */}
              <div className="p-3 bg-slate-900 rounded-lg space-y-2">
                <span className="text-xs text-slate-400 block">Defender Jersey</span>
                
                {/* Opponent Team Dropdown */}
                <select
                  value={selectedOpponentId || ''}
                  onChange={(e) => onSelectedOpponentChange?.(e.target.value || null)}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded text-sm text-white"
                >
                  <option value="">Custom Color</option>
                  {opponents.map(opp => (
                    <option key={opp.id} value={opp.id}>
                      {opp.name || opp.abbreviation || 'Team'}
                    </option>
                  ))}
                </select>
                
                {/* Color Preview / Manual Picker (only when no opponent selected) */}
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={defenderJerseyColor}
                    onChange={(e) => {
                      onDefenderJerseyColorChange?.(e.target.value);
                      onSelectedOpponentChange?.(null); // Clear opponent selection when manually changing color
                    }}
                    className="w-8 h-8 rounded cursor-pointer border-2 border-slate-600"
                    title="Defender jersey color"
                  />
                  <span className="text-xs text-slate-500 flex-1">
                    {selectedOpponentId 
                      ? opponents.find(o => o.id === selectedOpponentId)?.name || 'Team'
                      : defenderJerseyColor}
                  </span>
                </div>
                
                {opponents.length === 0 && (
                  <p className="text-xs text-slate-600 italic">
                    Add teams in Scouting to select their jersey colors
                  </p>
                )}
              </div>
              
              {/* Size Control */}
              <div className="flex items-center gap-3 p-3 bg-slate-900 rounded-lg">
                <span className="text-xs text-slate-400 w-10">Size</span>
                <input
                  type="range"
                  min="0.5"
                  max="1.2"
                  step="0.1"
                  value={defenderSize}
                  onChange={(e) => onDefenderSizeChange?.(parseFloat(e.target.value))}
                  className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-red-500"
                />
                <span className="text-sm text-red-400 font-bold w-10 text-right">
                  {defenderSize.toFixed(1)}
                </span>
              </div>
              
              {/* Defender List */}
              {defenders.length === 0 ? (
                <div className="text-center py-4 text-slate-500 text-sm">
                  No defenders added
                </div>
              ) : (
                <div className="space-y-2">
                  {defenders.map((def, idx) => {
                    const defSpeed = def.speed || 1.0;
                    const speedColor = defSpeed < 0.8 ? '#ef4444' : defSpeed > 1.2 ? '#22c55e' : '#f59e0b';
                    const guardedPlayer = positions.find(p => p.id === def.guardingId);
                    const isSelected = selectedDefender === def.id;
                    const isPending = pendingDefender === def.id; // v0.5.76: Pulsing when awaiting position
                    
                    return (
                      <div 
                        key={def.id} 
                        className={`rounded-lg overflow-hidden transition-all ${
                          isPending ? 'ring-2 ring-yellow-500 animate-pulse bg-yellow-900/30' :
                          isSelected ? 'ring-2 ring-red-500 bg-red-900/30' : 'bg-slate-900'
                        }`}
                      >
                        {/* Defender Row - Clickable */}
                        <button
                          onClick={() => onDefenderSelect?.(isSelected ? null : def.id)}
                          className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-800/50 transition-colors"
                        >
                          <div 
                            className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg flex-shrink-0 ${isPending ? 'animate-bounce' : ''}`}
                            style={{ backgroundColor: defenderJerseyColor, border: guardedPlayer ? `3px solid ${guardedPlayer.color}` : '3px solid #7f1d1d' }}
                          >
                            {guardedPlayer?.positionLabel || `D${idx + 1}`}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="text-white font-medium text-sm">
                              {isPending ? '⚡ Set Position!' : `Defender ${idx + 1}`}
                            </div>
                            <div className="text-xs text-slate-400">
                              {guardedPlayer ? `Guarding ${guardedPlayer.positionLabel}` : 'Unassigned'}
                            </div>
                          </div>
                          
                          {/* Selection indicator */}
                          {isSelected && (
                            <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs">
                              ✓
                            </div>
                          )}
                        </button>
                        
                        {/* v0.5.71: Selected Defender Actions */}
                        {isSelected && (
                          <div className="flex gap-2 px-3 py-2 bg-slate-950/50 border-t border-slate-800">
                            <button
                              onClick={() => onDefenderPathStart?.(def.id)}
                              className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-500 rounded text-white text-sm font-medium flex items-center justify-center gap-2"
                            >
                              <span>📍</span> Move
                            </button>
                            <button
                              onClick={() => onRemoveDefender?.(def.id)}
                              className="px-3 py-2 bg-red-900/50 hover:bg-red-800 rounded text-red-400 hover:text-white text-sm"
                              title="Remove"
                            >
                              ✕
                            </button>
                          </div>
                        )}
                        
                        {/* v0.5.40: Player Assignment Dropdown */}
                        <div className="flex items-center gap-2 px-3 py-2 bg-slate-950/50 border-t border-slate-800">
                          {/* v0.5.76: Static guarding display (auto-assigned by position order) */}
                          <span className="text-xs text-green-400">
                            ✓ Guarding {guardedPlayer?.positionLabel || 'unassigned'}
                          </span>
                        </div>
                        
                        {/* Speed Slider */}
                        <div className="flex items-center gap-2 px-3 py-2 bg-slate-950/50 border-t border-slate-800">
                          <span className="text-xs text-slate-500 w-8">
                            {defSpeed < 1 ? '🐢' : defSpeed > 1 ? '🐇' : '⚡'}
                          </span>
                          <input
                            type="range"
                            min="0.25"
                            max="2.0"
                            step="0.25"
                            value={defSpeed}
                            onChange={(e) => onDefenderUpdate?.(def.id, { speed: parseFloat(e.target.value) })}
                            className="flex-1 h-1.5 bg-slate-700 rounded appearance-none cursor-pointer"
                            style={{ accentColor: speedColor }}
                          />
                          <span 
                            className="text-xs font-bold w-12 text-right"
                            style={{ color: speedColor }}
                          >
                            {defSpeed.toFixed(2)}x
                          </span>
                        </div>
                        
                        {/* v0.5.76: Removed "Stop Following" control - defenders always follow their player */}
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Add Defender Button */}
              {defenders.length < 5 && (
                <button
                  onClick={() => onAddDefender?.()}
                  className="w-full py-3 bg-red-600/20 hover:bg-red-600/30 border-2 border-dashed border-red-600/50 rounded-lg text-red-400 font-medium text-sm transition-colors"
                >
                  + Add Defender ({defenders.length}/5)
                </button>
              )}
            </div>
          )}
        </div>

        {/* v0.5.39: Play Actions section moved to StepActionsPanel */}
      </div>
      
      {/* Version indicator */}
      <div 
        data-element="sidepanel-version"
        style={{
          padding: '8px 12px',
          backgroundColor: '#1f2937',
          borderTop: '1px solid #374151',
          fontSize: '10px',
          color: '#6b7280',
          textAlign: 'center',
          fontFamily: 'monospace'
        }}
      >
        v0.5.105
      </div>
      
      {/* Scroll indicator */}
      <div className="h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-red-500 opacity-50" data-element="sidepanel-scroll-indicator" />
    </div>
  );
};

export default SidePanel;
